import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { RideCard } from "@/components/RideCard";
import { RideMap } from "@/components/RideMap";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Rocket, SlidersHorizontal } from "lucide-react";

const SearchResults = () => {
  const [params] = useSearchParams();
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const date = params.get("date") || "";
  const seats = params.get("seats") || "1";

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("date");
  const [maxPrice, setMaxPrice] = useState(3000);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = { seats, sort };
    if (from) q.from_city = from;
    if (to) q.to_city = to;
    if (date) q.date = date;
    api.get("/rides", { params: q })
      .then((r) => { setRides(r.data); setSelected(null); })
      .catch(() => setRides([]))
      .finally(() => setLoading(false));
  }, [from, to, date, seats, sort]);

  const filtered = useMemo(() => rides.filter((r) => r.price_per_seat <= maxPrice), [rides, maxPrice]);

  const routes = useMemo(() => filtered
    .filter((r) => r.from_coords && r.to_coords)
    .map((r) => ({
      from: r.from_coords, to: r.to_coords, fromLabel: r.from_city, toLabel: r.to_city,
      color: selected && selected.ride_id === r.ride_id ? "#22D3EE" : "#7C3AED",
    })), [filtered, selected]);

  const ListPanel = (
    <div className="space-y-3" data-testid="search-results-list">
      {loading ? (
        Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 w-full bg-white/5 rounded-[var(--radius)]" />)
      ) : filtered.length === 0 ? (
        <Card className="glass p-10 text-center">
          <Rocket className="mx-auto text-[#7C3AED] mb-3" size={36} />
          <h3 className="font-display text-xl">No rides in this orbit yet</h3>
          <p className="text-muted-cosmic mt-2 text-sm">Try a different date or route — or publish your own ride and lead the way.</p>
        </Card>
      ) : (
        filtered.map((r) => (
          <RideCard key={r.ride_id} ride={r} selected={selected?.ride_id === r.ride_id} onSelect={setSelected} />
        ))
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <SearchWidget initial={{ from, to, date, seats }} className="mb-6" />

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold">{from || "All"} {to ? `→ ${to}` : "rides"}</h1>
          <p className="text-sm text-muted-cosmic">{loading ? "Scanning the galaxy…" : `${filtered.length} ride${filtered.length === 1 ? "" : "s"} found`}{date ? ` · ${date}` : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={15} className="text-[#22D3EE]" />
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="search-sort-select" className="h-10 w-40 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-solid text-white">
                <SelectItem value="date">Earliest date</SelectItem>
                <SelectItem value="price">Lowest price</SelectItem>
                <SelectItem value="time">Departure time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card className="glass p-4 mb-4">
        <div className="flex items-center justify-between mb-2"><span className="text-sm text-secondary-cosmic">Max price</span><span className="font-display text-[#FFF7D6]">{maxPrice} ฿</span></div>
        <Slider data-testid="search-price-slider" value={[maxPrice]} onValueChange={(v) => setMaxPrice(v[0])} min={50} max={3000} step={50} />
      </Card>

      {/* Desktop split view */}
      <div className="hidden lg:grid grid-cols-12 gap-4">
        <div className="col-span-5 max-h-[70vh] overflow-y-auto pr-1">{ListPanel}</div>
        <div className="col-span-7 sticky top-20 h-[70vh]">
          <RideMap routes={routes} height="100%" />
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="lg:hidden">
        <Tabs defaultValue="list">
          <TabsList className="grid grid-cols-2 bg-white/5 w-full mb-4">
            <TabsTrigger value="list">List</TabsTrigger>
            <TabsTrigger value="map">Map</TabsTrigger>
          </TabsList>
          <TabsContent value="list">{ListPanel}</TabsContent>
          <TabsContent value="map"><RideMap routes={routes} height={420} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SearchResults;
